import _ from 'lodash'
import Backbone from 'backbone'
import reportConstants from 'scoreboard/reports/report-constants'
import ReportBreakdown from './designer-breakdown'
import unwrapModel from 'scoreboard/components/unwrap-model'

/**
 * Client side model of a stack using the repeat by structure
 */
class ReportRepeatBy extends Backbone.Model {

	initialize(attrs, opts = {}) {
		this._options = opts

		// bubble the breakdown model events up
		this.listenTo(this._breakdowns, 'all', (...args) => {
			let eventName = args[0]
			if (eventName == 'update') {
				let eventOpts = args[2]
				//keep the header order in sync
				if (!_.isEmpty(eventOpts.changes.removed)) {
					let { headerOrder } = this.attributes
					//since we're only removing breakdowns here, account for the
					//'field row' in the header order when removing the correct index
					if (headerOrder[0] == reportConstants.HEADER_TYPE_FIELD_ROW) {
						headerOrder.splice(1, 1)	//field is the top row
					} else if (_.last(headerOrder) == reportConstants.HEADER_TYPE_FIELD_ROW) {
						headerOrder.splice(0, 1)	//field is the bottom row
					} else {
						//field is the middle, if the index points to the field,
						//adjust up by one (the remove the bottom breakdown)
						let adjustedIndex = headerOrder[eventOpts.index] == reportConstants.HEADER_TYPE_FIELD_ROW ?
							eventOpts.index+1 : eventOpts.index
						headerOrder.splice(adjustedIndex, 1)
					}
				}
			} else if (eventName == 'remove' && _.isEmpty(this.attributes.breakdowns)) {
				this.destroy()  //destroy the repeat by if no more breakdown
				return
			}

			this.trigger.apply(this, args)
		})
	}

	timeRange() {
		let periodRange = _.find(this.attributes.breakdowns, model => model.has('range'))
		return periodRange ? periodRange.get('range') : null
	}

	reorderHeader(headerIndexToMove, newHeaderIndex) {
		let { headerOrder } = this.attributes
		//check if we're swapping breakdown order
		let swapBreakdownsRows = headerOrder[headerIndexToMove] == headerOrder[newHeaderIndex]
		if (swapBreakdownsRows) {
			//if we're rearranging the breakdown rows, swap silently, event at end
			let breakdownModel = this._breakdowns.at(0)
			this._breakdowns.remove(breakdownModel, {silent: true})
			this._breakdowns.add(breakdownModel, {at: 1, silent: true})
		}

		//do the reorder (will fire off event to update the parent models)
		let newHeaderOrder = [].concat(headerOrder)
		newHeaderOrder.splice(newHeaderIndex, 0, newHeaderOrder.splice(headerIndexToMove, 1)[0])
		this.unset('headerOrder', {silent: true})	//force the event to trigger
		this.set('headerOrder', newHeaderOrder)
	}

	/**
	 * Adds a breakdown to this repeat by structure
	 *
	 * @param {String|Model} breakdownBy the field id (or Backbone Model
	 * representing the breakdown) to breakdown by
	 */
	addBreakdown(breakdownBy) {
		let breakdownByAttributes = unwrapModel(breakdownBy)

		//update the header first, since the add will trigger an event update
		this.attributes.headerOrder.splice(0, 0, reportConstants.HEADER_TYPE_BREAKDOWN_ROW)
		this._breakdowns.add(new ReportBreakdown(breakdownByAttributes), {at: 0})
	}

	/**
	 * Removes a breakdown from this repeat by structure
	 * @param {Array|ReportBreakdown} breakdown
	 */
	removeBreakdown(breakdown, opts) {
		if (!breakdown) { return }
		if (_.isArray(breakdown)) {
			//copy into separate array to prevent any concurrent modifications
			_.each([].concat(breakdown), b => this.removeBreakdown(b, opts))
			return
		}

		if (this._breakdowns.models.length == 1) {
			this.destroy(opts)
		} else {
			let currentHeaderIndex = 0
			let indexToRemove = -1
			_.each(this._breakdowns.models, model => {
				while (this.attributes.headerOrder[currentHeaderIndex] != reportConstants.HEADER_TYPE_BREAKDOWN_ROW) {
					currentHeaderIndex++
				}
				if (model === breakdown) {
					indexToRemove = currentHeaderIndex
				}
				currentHeaderIndex++
			})
			this.attributes.headerOrder.splice(indexToRemove, 1)
			this._breakdowns.remove(breakdown, opts)
		}
	}

	transientBreakdowns() {
		return this._breakdowns.where({transient: true})
	}

	parse(attrs, opts) {
		let { headerOrder, breakdowns, sortAggregationType } = attrs
		let models = _.map(breakdowns, bd => new ReportBreakdown(bd))

		if (this._breakdowns) {
			this._breakdowns.reset(models, opts)
		} else {
			this._breakdowns = new Backbone.Collection(models, opts)
		}
		return {
			headerOrder,
			breakdowns: this._breakdowns.models,
			sortAggregationType
		}
	}

	/**
	 * Returns the field ids/column types this repeat by is
	 * breaking down by in order (if any)
	 */
	allBreakdownBys() {
		return _(this.attributes.breakdowns)
			.map(bd => bd.get('breakdownBy'))
			.filter(fieldIdOrColumnType => !_.isNil(fieldIdOrColumnType))
			.value()
	}

	toJSON() {
		//don't send empty breakdowns
		if (_.isNil(this.attributes.breakdowns) || _.isEmpty(this.attributes.breakdowns)) {
			return null
		}

		return {
			headerOrder: this.attributes.headerOrder,
			breakdowns: _.map(this.attributes.breakdowns, model => model.toJSON()),
			sortAggregationType: this.attributes.sortAggregationType
		}
	}

	warnings() {
		return _.flatMap(this.attributes.breakdowns, model => model.warnings())
	}

}

export default ReportRepeatBy
ReportRepeatBy.__filename__ = 'reports/common/model/designer-repeat-by.js'
